import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: '600+', label: 'Incidents Tracked' },
  { value: '90+',  label: 'Species Monitored' },
  { value: '80+',  label: 'Convictions Logged' },
  { value: '5',    label: 'Countries Active'  },
];

export default function AboutUs1() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="w-full bg-[#fafaf7] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* ── Stacked image block ── */}
          <div className={`relative reveal ${visible ? 'visible' : ''}`}>
            <div className="relative h-[520px]">
              {/* Back image */}
              <div
                className="absolute top-0 right-0 w-4/5 h-4/5 rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 32px 64px rgba(5,46,22,0.18)' }}
              >
                <img
                  src="/images/leopard.jpg"
                  alt="Leopard in the wild"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#052e16]/55 to-transparent" />
              </div>
              {/* Front image */}
              <div
                className="absolute bottom-0 left-0 w-3/5 h-3/5 rounded-2xl overflow-hidden border-4 border-[#fafaf7]"
                style={{ boxShadow: '0 20px 48px rgba(5,46,22,0.2)' }}
              >
                <img
                  src="/images/elephant.jpg"
                  alt="Elephant in savanna"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Accent badge */}
              <div
                className="absolute top-6 left-6 bg-[#052e16] text-green-400 rounded-xl px-4 py-3"
                style={{ fontFamily: 'var(--font-body)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}
              >
                Est. 2022 &mdash; LAPA Africa
              </div>
            </div>
          </div>

          {/* ── Text block ── */}
          <div className="flex flex-col gap-10">
            <div className={`reveal reveal-delay-1 ${visible ? 'visible' : ''}`}>
              <span className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-green-600" />
                <span
                  className="text-green-700 text-xs tracking-[0.2em] uppercase font-medium"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  Our Mission
                </span>
              </span>
              <h2
                className="text-4xl lg:text-5xl font-bold text-gray-900 leading-[1.1] mb-6"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Built to protect what
                <br />
                <span className="text-green-700">cannot speak for itself</span>
              </h2>
              <p
                className="text-gray-600 text-base leading-relaxed max-w-lg"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                WITS improves data accuracy, detection of illegal wildlife activities, and
                transparency across Africa. By enabling real-time tracking of incidents and
                conservation interventions, WITS empowers decision-makers with the evidence
                they need to act quickly and effectively.
              </p>
            </div>

            {/* Stats grid */}
            <div className={`grid grid-cols-2 gap-px bg-green-100 rounded-2xl overflow-hidden reveal reveal-delay-2 ${visible ? 'visible' : ''}`}>
              {stats.map((s) => (
                <div key={s.label} className="bg-[#fafaf7] px-8 py-6">
                  <p
                    className="text-3xl font-bold text-green-700 mb-1"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {s.value}
                  </p>
                  <p
                    className="text-xs text-gray-500 tracking-wide uppercase"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            <div className={`reveal reveal-delay-3 ${visible ? 'visible' : ''}`}>
              <a
                href="/features"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-200"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Explore the Platform &rarr;
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
